import { useMemo } from 'react';
import { useSync } from '@tldraw/sync';
import { TLAssetStore, TLUserPreferences, uniqueId } from 'tldraw';

const SYNC_BASE_URL = `${location.origin}/tldraw`;

/**
 * Asset store for tldraw — handles upload/download of large binary assets
 * (images, videos) through the self-hosted sync server.
 * PDF page images (base64 inline) bypass this since they're embedded in shape props.
 */
const multiplayerAssetStore: TLAssetStore = {
  async upload(_asset, file) {
    const ext = file.name ? file.name.split('.').pop() : 'png';
    const id = `${uniqueId()}.${ext}`;
    await fetch(`${SYNC_BASE_URL}/uploads/${id}`, {
      method: 'PUT',
      body: file
    });
    return { src: `${SYNC_BASE_URL}/uploads/${id}` };
  },
  resolve(asset) {
    return asset.props.src;
  }
};

/**
 * Shared hook wrapping `useSync` for connecting to the self-hosted tldraw sync server.
 * Replaces `useSyncDemo` which connected to tldraw's public demo server.
 *
 * Returns the same shape as useSyncDemo: { status: 'loading' | 'synced-remote' | 'error', store?, error? }
 */
export function useMultiplayerSync(roomId: string, userInfo?: TLUserPreferences) {
  const wsProtocol = location.protocol === 'https:' ? 'wss:' : 'ws:';

  const syncOptions = useMemo(
    () => ({
      uri: `${wsProtocol}//${location.host}/tldraw/connect/${roomId}`,
      assets: multiplayerAssetStore,
      userInfo
    }),
    [wsProtocol, roomId, userInfo]
  );

  return useSync(syncOptions);
}
